import { derived } from 'svelte/store';
import { persisted } from '../stores/persisted';
import { useFetchClient } from '@foundatiofx/fetchclient';
import { goto } from '$app/navigation';
import { match } from '$app/paths';
import { page } from '$app/state';
import { z } from 'zod';
import { ClientApi, type ClientSummary, clientSummarySchema } from '#api/clientApi.ts';
import {
    type AuthenticatedUser,
    authenticatedUserSchema,
    type LoginResponse,
    loginResponseSchema,
    type MfaAuthenticatorInfo,
    type MfaProvider,
    type MfaStatusResponse
} from './userApi';
import { authStatus, initAuthenticatedStores, resetAuthenticatedStores } from '#lib/stores/stores.ts';
import currentObligationId from '#lib/stores/currentObligationId.ts';
import { clearAutopayPromptDismissals } from '#lib/stores/autopayPromptDismissal.ts';
import { getTabClientId, setTabClientId } from '#lib/utils/tabClientId.ts';
import { stopEventHub } from '#api/eventHub.ts';

export const accessToken = persisted<string | null>('accessToken', null, {
    storage: 'local'
});
export const associatedClientSummaries = persisted<ClientSummary[] | null>('associatedClientSummaries', null, {
    storage: 'local'
});
export const currentUser = persisted<AuthenticatedUser | null>('currentUser', null, { storage: 'local' });

const clientApi = new ClientApi();

export function clientChanged() {
    refreshAssociatedClientSummaries();
}

async function getMe(refresh = false) {
    let url = `auth/me`;
    if (refresh) url += '?refresh=true';

    // Claimed here so a dead session never reaches the 401 middleware, whose probe calls back in.
    const user = await useFetchClient().getJSON<AuthenticatedUser>(url, {
        expectedStatusCodes: [200, 401, 404]
    });
    if (user.ok && authenticatedUserSchema.safeParse(user.data).success) {
        return user;
    } else {
        return null;
    }
}

let sessionProbe: Promise<boolean> | null = null;

/**
 * Whether the API still treats this session as authenticated. `currentUser` cannot answer it — that
 * store is persisted, so it reports whatever the browser last wrote.
 *
 * Concurrent callers share one probe: a page load that fires several requests can fail several times
 * over, and each failure must not cost its own round trip.
 */
export function sessionIsAlive(): Promise<boolean> {
    sessionProbe ??= probeSession().finally(() => {
        sessionProbe = null;
    });

    return sessionProbe;
}

async function probeSession(): Promise<boolean> {
    try {
        const response = await useFetchClient().getJSON<AuthenticatedUser>('auth/me', {
            expectedStatusCodes: [200, 401, 404]
        });

        return response.ok;
    } catch {
        // Fails open: a probe that never completed is no evidence of a logout.
        return true;
    }
}

export const isAuthenticated = derived(currentUser, ($currentUser) => $currentUser !== null);

const unauthenticatedGroups = /^\/\((auth|open)\)(\/|$)/;

// Returns the login URL to send an unauthenticated user to, or null when the current route is
// already an (auth)/(open) page (so callers don't redirect-loop on the login page itself).
export async function loginRedirectTarget(): Promise<string | null> {
    // `page` has no route until hydration finishes, so a 401 from the first load matches the address bar.
    const routeId = page.route.id ?? (await match(location.href))?.id;
    if (!routeId || unauthenticatedGroups.test(routeId)) return null;

    return location.pathname === '/' ? '/login' : `/login?redirect=${location.href}`;
}

export async function gotoLogin() {
    const url = await loginRedirectTarget();
    if (!url) return;

    // The only writer of this on a mid-session death now that the layout middlewares are gone.
    // PublicRoute both gates the login form on it and bounces to / while it still reads authenticated.
    authStatus.set('unauthenticated');

    await goto(url, { replace: true });
}

export async function login(email: string, password: string, rememberMe: boolean) {
    const result = await useFetchClient().postJSON<LoginResponse>(
        `auth/login?cookieMode=true&isPersistent=${rememberMe}`,
        {
            email,
            password
        },
        {
            expectedStatusCodes: [200, 401]
        }
    );

    if (
        result.status === 200 &&
        loginResponseSchema.safeParse(result.data) &&
        result.data?.success &&
        result.data?.user
    ) {
        associatedClientSummaries.set(null);
        currentUser.set(result.data.user!);
        if (currentUser.get() != null) await updateAssociatedClientSummaries();

        authStatus.set('authenticated');
        await initAuthenticatedStores();
    }

    return result;
}

export async function forgotPassword(email: string) {
    return useFetchClient().post('auth/forgotPassword', { email });
}

export async function resetPassword(email: string, newPassword: string, resetCode: string) {
    return await useFetchClient().post(
        'auth/resetpassword',
        { email, newPassword, resetCode },
        { expectedStatusCodes: [200, 400] }
    );
}

export async function changeExpiredPassword(email: string, currentPassword: string, newPassword: string) {
    return await useFetchClient().post(
        'auth/changeExpiredPassword',
        { email, currentPassword, newPassword },
        { expectedStatusCodes: [200, 400, 401] }
    );
}

export async function logout() {
    accessToken.clear();
    currentUser.clear();

    // Dynamic import: recentItems statically imports this module, so a static import here
    // would create a cycle whose init order breaks at startup.
    const { clearRecentItems } = await import('#lib/stores/recentItems.ts');
    clearRecentItems();

    associatedClientSummaries.clear();
    removeLegacyAssociatedClients();
    currentObligationId.clear();
    clearAutopayPromptDismissals();
    setTabClientId(null);
    resetAuthenticatedStores();
    await stopEventHub();

    await useFetchClient().delete('auth/logout', {
        expectedStatusCodes: [200, 401]
    });

    authStatus.set('unauthenticated');
}

export function getCurrentUser(): AuthenticatedUser | null {
    return currentUser.get();
}

// Always resolves through auth/me: the persisted copy outlives the cookie, and the ClientId header
// middleware scopes the answer to this tab. Only a definitive answer moves the persisted user — like
// probeSession, a probe that never completed (5xx, network) is no evidence of a logout.
export async function updateCurrentUser() {
    let result: Awaited<ReturnType<typeof getMe>>;
    try {
        result = await getMe();
    } catch {
        // The shared copy may have been re-scoped by another tab; with no answer to correct it, this
        // tab must not render it under a different client.
        const tabClientId = getTabClientId();
        if (tabClientId != null && tabClientId !== currentUser.get()?.clientId) currentUser.set(null);
        return;
    }

    removeLegacyAssociatedClients();

    const user = result && result.ok && authenticatedUserSchema.safeParse(result.data).success ? result.data : null;
    // Dropped before the user switches, so another user's cached list never renders under this one.
    if (user?.id !== currentUser.get()?.id) associatedClientSummaries.set(null);
    currentUser.set(user);

    const authenticatedUser = currentUser.get();

    if (authenticatedUser && !authenticatedUser.isObligor) {
        if (associatedClientSummaries.get() != null) refreshAssociatedClientSummaries();
        else await updateAssociatedClientSummaries();
    }
}

export async function refreshCurrentUser() {
    const result = await getMe(true);
    if (result) currentUser.set(result.data);
}

export async function userUpdated({ detail }: any) {
    const user = currentUser.get();
    if (user != null && user.id == detail.id) {
        await refreshCurrentUser();

        if (currentUser.get() != null) await updateAssociatedClientSummaries();
    }
}

export function userDeleted({ detail }: CustomEvent<{ id: number; name: string }>) {
    const user = currentUser.get();
    if (user != null && user.id == detail.id) {
        logout();
    }
}

async function updateAssociatedClientSummaries() {
    const forUserId = currentUser.get()?.id;
    const result = await clientApi.list({ page: 1, limit: 9999, summary: true });
    // A late response for a user who has since switched must not land in the next user's store.
    if (!result.ok || currentUser.get()?.id !== forUserId) return;
    associatedClientSummaries.set(z.array(clientSummarySchema).parse(result.data?.results ?? []));
}

function refreshAssociatedClientSummaries() {
    updateAssociatedClientSummaries().catch((error) => console.error('[auth] Client summary refresh failed', error));
}

function removeLegacyAssociatedClients() {
    try {
        localStorage.removeItem('associatedClients');
    } catch {
        // Storage can be unavailable (privacy mode, no window); the stale key is harmless there.
    }
}

export async function setupMfaAuthenticator() {
    return await useFetchClient().getJSON<MfaAuthenticatorInfo>(`auth/setup-mfa-authenticator`);
}

export async function getMfaStatus() {
    return await useFetchClient().getJSON<MfaStatusResponse>(`auth/mfa-status`);
}

export async function disableMfa(provider: MfaProvider | null = null) {
    return await useFetchClient().postJSON('auth/disable-mfa', { provider }, { expectedStatusCodes: [200, 401] });
}

export async function enableMfa(code: string | null = null) {
    return await useFetchClient().postJSON('auth/enable-mfa', { code }, { expectedStatusCodes: [201, 401] });
}

export async function mfaLogin(
    email: string,
    mfaCode: string,
    provider: MfaProvider,
    rememberMe: boolean,
    rememberMachine: boolean
) {
    const result = await useFetchClient().postJSON<LoginResponse>(
        `auth/verify-mfa?cookieMode=true`,
        {
            provider,
            code: mfaCode,
            email,
            rememberMe,
            rememberMachine
        },
        {
            expectedStatusCodes: [200, 401]
        }
    );
    if (
        result.status === 200 &&
        loginResponseSchema.safeParse(result.data) &&
        result.data?.success &&
        result.data?.user
    ) {
        associatedClientSummaries.set(null);
        currentUser.set(result.data.user!);
        if (currentUser.get() != null) await updateAssociatedClientSummaries();

        authStatus.set('authenticated');
        await initAuthenticatedStores();
    }

    return result;
}

export async function sendMfaEmail(email: string) {
    return await useFetchClient().postJSON(
        `auth/send-mfa-email`,
        {
            email
        },
        {
            expectedStatusCodes: [200]
        }
    );
}

// The Concord client that owns global resources like blueprint products (RoleConstants.GlobalClientId).
export const globalClientId = 1;

// roles
export const viewerRole = 'Viewer';
export const editorRole = 'Editor';
export const complianceRole = 'Compliance';
export const supervisorRole = 'Supervisor';
export const treasuryRole = 'Treasury';
export const managerRole = 'Manager';
export const adminRole = 'Admin';

// obligor roles
export const obligorRole = 'Obligor';
export const viewObligorRole = 'ViewObligor';

// isolated roles
export const secretRevealerRole = 'SecretRevealer';
export const collectorRole = 'Collector';
export const reportManagerRole = 'ReportManager';

export function IsGlobalAdmin(): boolean {
    return currentUser.get()?.globalRoles?.includes(adminRole) ?? false;
}

export function IsGlobalReportManager(): boolean {
    return IsInGlobalRole([adminRole, reportManagerRole]);
}

export function IsInGlobalRole(roles: string[]): boolean {
    return roles.some((role) => {
        if (!role || role.length == 0) return true;
        return currentUser.get()?.globalRoles?.includes(role);
    });
}

export function IsInClientRole(roles: string[]): boolean {
    return roles.some((role) => {
        if (!role || role.length == 0) return true;
        return currentUser.get()?.roles?.includes(role);
    });
}

export function IsInRole(roles: string[]): boolean {
    return IsInGlobalRole(roles) || IsInClientRole(roles);
}

export function IsAdmin(): boolean {
    const roles = [adminRole];
    return IsInRole(roles);
}

export function IsEditor(): boolean {
    const roles = [adminRole, editorRole];
    return IsInRole(roles);
}

export function IsViewer(): boolean {
    const roles = [adminRole, editorRole, viewerRole];
    return IsInRole(roles);
}

export function IsTreasury(): boolean {
    const roles = [adminRole, treasuryRole];
    return IsInRole(roles);
}

export function IsSecretRevealer(): boolean {
    return IsInRole([secretRevealerRole]);
}

export function IsSupervisor(): boolean {
    const roles = [adminRole, managerRole, treasuryRole, supervisorRole];
    return IsInRole(roles);
}

export function HasRestrictions(): boolean {
    const user = currentUser.get();
    if (user == null) return false;

    return user.hasLenderRestriction || user.hasProductRestriction;
}

export function canAccessLender(lenderId: number | null | undefined): boolean {
    const user = currentUser.get();
    if (user == null) return false;
    if (!user.hasLenderRestriction) return true;
    if (lenderId == null) return false;

    return user.lenderIds.includes(lenderId);
}

export function canAccessProduct(productId: number | null | undefined): boolean {
    const user = currentUser.get();
    if (user == null) return false;
    if (!user.hasProductRestriction) return true;
    if (productId == null) return false;

    return user.productIds.includes(productId);
}
